import { isNative } from './capacitor';

// ── Hand a generated file to the user, on web and in the app ─────────────────
// A blob + `<a download>` click is inert inside the Capacitor WebView: the tap
// does nothing, no file appears, and no error is raised — the button just stops
// spinning. Native builds have to write the bytes to the filesystem and pass a
// URI to the share sheet instead.
//
// This existed in three places (the library's PDF export, the reader's
// illustration save, the story-card share) and was MISSING from the reader's
// PDF export, so "Export PDF" silently did nothing there while working from the
// library. Copy #4 would have gone the same way; one implementation cannot.
//
// Returns 'shared' | 'downloaded' | 'cancelled' so callers only fire analytics
// for a file the user actually took.
export const saveOrShareFile = async (blob, filename, { title, dialogTitle } = {}) => {
  if (isNative()) {
    try {
      const { Filesystem, Directory } = await import('@capacitor/filesystem');
      const { Share } = await import('@capacitor/share');

      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(String(reader.result).split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });

      // Cache, not Documents: this is a hand-off, and the OS reclaims it.
      await Filesystem.writeFile({ path: filename, data: base64, directory: Directory.Cache });
      const { uri } = await Filesystem.getUri({ path: filename, directory: Directory.Cache });
      await Share.share({ title, files: [uri], dialogTitle: dialogTitle || 'Save' });
      return 'shared';
    } catch (err) {
      // Dismissing the share sheet is a choice, not a failure.
      const m = String(err?.message || '').toLowerCase();
      if (m.includes('cancel') || m.includes('abort')) return 'cancelled';
      // Anything else (plugin missing, write refused): fall through to the web
      // path, which at worst is the same no-op we already had.
    }
  }

  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  } finally {
    URL.revokeObjectURL(url);
  }
  return 'downloaded';
};

export default saveOrShareFile;
